import { AthosSearchProvider } from 'libs/athos/fetchers/search'

import clsx from 'clsx'

import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { useScrollCache } from 'libs/athos/hooks/useScrollCache'
import { useFilterScroll } from 'libs/athos/hooks/useFilterScroll'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { BeaconAnalyticsProvider, useBeaconLogImpressions, useBeaconLogRender } from 'libs/athos/hooks/useBeaconAnalytics'
import { useMerchedResults } from 'libs/athos/hooks/useMerchedResults'
import { Pagination } from 'libs/athos/components/collection/Pagination'
import { useOption } from 'libs/athos/hooks/useOption'
import { FiltersProvider } from 'libs/athos/hooks/useFilters'
import { CollectionToolbar } from 'libs/athos/components/collection/CollectionToolbar'
import { FilterDrawer } from 'libs/athos/components/collection/filters/FilterDrawer'
import { SearchHeader } from 'libs/athos/components/SearchHeader'
import { ResultItem } from 'libs/athos/components/ResultCardWrapper'
import { SkeletonCard } from 'libs/athos/components/SkeletonCard'
import { calculateProductCount } from 'libs/athos/components/collection/filters/ProductCount'

const GRID_IMAGE_SIZES = '(min-width: 1828px) 25vw, (min-width: 768px) 33vw, 100vw'
const GRID_IMAGE_WIDTHS = [200, 320, 480, 640, 800, 1000, 1200, 1440]

function SearchGrid({ showFilters }) {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const { results, isLoading } = useAthosContext()
  const currentResults = useMerchedResults()
  const { setScrollElement } = useFilterScroll()
  const hasStartedLoading = useRef(false)
  const [beaconNode, setBeaconNode] = useState()
  const query = useOption(
    'query',
    { syncWithURL: true }
  )

  useBeaconLogRender()
  useBeaconLogImpressions(beaconNode)

  const setGridNode = useCallback((el) => {
    setScrollElement(el)
    setBeaconNode(el)
  }, [setScrollElement])

  const { corrected = '', original = '' } = results?.query || {}
  const didYouMean = results?.didYouMean?.query || ''

  const corrections = {
    corrected,
    original,
    didYouMean
  }

  useEffect(() => {
    if (isLoading) hasStartedLoading.current = true
    if (!isLoading && hasStartedLoading.current) setFirstLoad(false)
  }, [isLoading])

  // Show skeletons only while there are no results to display yet, so the grid
  // always swaps to real results as soon as they arrive (independent of the
  // loading flags settling).
  const showSkeleton = currentResults.length === 0 && (isLoading || firstLoad)

  useScrollCache([currentResults, firstLoad])

  const totalResults = calculateProductCount(results)

  return (
    <div className="grow flex flex-col">
      <SearchHeader query={query} totalResults={totalResults} isLoading={isLoading} corrections={corrections} results={results}/>
      <FiltersProvider>
        {(currentResults.length > 0 || firstLoad) && (
          <>
            <CollectionToolbar />
            {totalResults > 9 && (
              <x-back-to-top className="lg-max:!hidden sticky top-view left-margin size-0 z-40">Back To Top</x-back-to-top>
            )}
            <div className="grow flex flex-col md:grid md:grid-cols-min-full items-stretch">
              <FilterDrawer showFilters={showFilters} />
              <div className="grow flex flex-col h-auto lg:border-l lg:border-borders-default lg-max:pb-4.875">
                <div
                  ref={setGridNode}
                  className={clsx('grid grid-cols-2 md:grid-cols-3 xxl:grid-cols-4 image-view-small:lg:grid-cols-4 image-view-small:xxl:grid-cols-5 auto-rows-min [grid-auto-flow:row_dense] gap-x-gutter gap-y-0.25 w-full', (isLoading && firstLoad) && 'min-h-screen-height')}
                >
                  {showSkeleton ? (
                    Array.from({ length: window.settings?.per_page || 12 }, (_, index) => (
                      <SkeletonCard key={`skeleton-${index}`} className="w-full" />
                    ))
                  ) : (
                    currentResults?.map((item, index) => {
                      return <ResultItem item={item} key={item.handle || `banner-${index}`} className="wk-wishlist-button-target" dataIndex={index.toString()} eager={index === 0} imageSizes={GRID_IMAGE_SIZES} imageWidths={GRID_IMAGE_WIDTHS} mobileSlider />
                    })
                  )}
                </div>
                <Pagination />
              </div>
            </div>
          </>
        )}
      </FiltersProvider>
    </div>
  )
}

export function WrappedSearchGrid({ 'show-filters': showFilters }) {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')
  return (
    <AthosSearchProvider query={queryParam}>
      <BeaconAnalyticsProvider type="search">
        <SearchGrid showFilters={showFilters === 'true'} />
      </BeaconAnalyticsProvider>
    </AthosSearchProvider>
  )
}
